import React, { useState } from "react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { Link } from "react-router-dom";

import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import permissionsService from "@/services/permissionsService";

import { useMyRegistrations } from "../../hooks/useMyRegistrations";
import MyRegistrationCard from "../MyRegistrationCard/MyRegistrationCard";
import { MyRegistrationItem } from "../../api/getMyRegistrations/types";

function SectionHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-baseline justify-between mb-3">
      <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </h2>
      <span className="text-xs text-gray-400 dark:text-gray-500">
        {count} {count === 1 ? "registration" : "registrations"}
      </span>
    </div>
  );
}

function Section({
  label,
  items,
  variant = "active",
  canManageRegistrations,
  onCancelSuccess,
}: {
  label: string;
  items: MyRegistrationItem[];
  variant?: "active" | "past";
  canManageRegistrations: boolean;
  onCancelSuccess: () => void;
}) {
  if (items.length === 0) return null;
  return (
    <section className="space-y-3">
      <SectionHeader count={items.length} label={label} />
      <div className="space-y-3">
        {items.map((r) => (
          <MyRegistrationCard
            canManageRegistrations={canManageRegistrations}
            key={`${r.sessionUuid}-${r.enrollments[0]?.enrollmentId ?? ""}`}
            onCancelSuccess={onCancelSuccess}
            registration={r}
            variant={variant}
          />
        ))}
      </div>
    </section>
  );
}

function CollapsibleSection({
  label,
  items,
  variant,
  canManageRegistrations,
  onCancelSuccess,
}: {
  label: string;
  items: MyRegistrationItem[];
  variant: "active" | "past";
  canManageRegistrations: boolean;
  onCancelSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;

  return (
    <section className="space-y-3">
      <button
        aria-expanded={open}
        className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        {label}
        <span className="text-xs text-gray-400 dark:text-gray-500 normal-case tracking-normal">
          ({items.length})
        </span>
      </button>
      {open && (
        <div className="space-y-3">
          {items.map((r) => (
            <MyRegistrationCard
              canManageRegistrations={canManageRegistrations}
              key={`${r.sessionUuid}-${r.enrollments[0]?.enrollmentId ?? ""}`}
              onCancelSuccess={onCancelSuccess}
              registration={r}
              variant={variant}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function LegacyPortalInfo() {
  const memberPortalUrl = import.meta.env.VITE_MEMBER_PORTAL_URL;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label="About this page"
          className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          type="button"
        >
          <Info className="h-3.5 w-3.5" />
          Where are my older registrations?
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-sm" side="bottom">
        <p className="text-gray-700 dark:text-gray-300">
          This page only shows registrations made through the new event
          registration system. Registrations made through the legacy portal are
          managed there.
        </p>
        {memberPortalUrl && (
          <a
            className="mt-2 inline-block text-sm font-medium text-primary hover:underline"
            href={memberPortalUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            Open the legacy portal →
          </a>
        )}
      </PopoverContent>
    </Popover>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 dark:border-slate-700 px-6 py-16 text-center">
      <h2 className="text-base font-normal text-gray-900 dark:text-white">
        No registrations yet
      </h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Browse upcoming events and register yourself or your team.
      </p>
      <Link
        className="mt-4 inline-flex items-center text-sm font-medium text-primary hover:underline"
        to="/event-registration/events"
      >
        Browse events →
      </Link>
    </div>
  );
}

const MyRegistrationsPage: React.FC = () => {
  const { groups, upcomingCount, pastCount, loading, error, refetch } =
    useMyRegistrations();
  const { hasPermission } = permissionsService();
  const canManageRegistrations = hasPermission("event-registration");

  return (
    <MainPageWrapper loading={loading} title="My Registrations">
      <div className="mb-6 flex justify-end">
        <LegacyPortalInfo />
      </div>

      {error && (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      )}

      {!error && !loading && (
        <>
          {upcomingCount === 0 && pastCount === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-10">
              <Section
                canManageRegistrations={canManageRegistrations}
                items={groups.thisWeek}
                label="This week"
                onCancelSuccess={refetch}
              />
              <Section
                canManageRegistrations={canManageRegistrations}
                items={groups.laterThisMonth}
                label="Later this month"
                onCancelSuccess={refetch}
              />
              <Section
                canManageRegistrations={canManageRegistrations}
                items={groups.future}
                label="Future"
                onCancelSuccess={refetch}
              />
              {upcomingCount === 0 && (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-slate-700 px-6 py-10 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No upcoming registrations.{" "}
                    <Link
                      className="text-primary hover:underline"
                      to="/event-registration/events"
                    >
                      Browse events →
                    </Link>
                  </p>
                </div>
              )}
              <CollapsibleSection
                canManageRegistrations={canManageRegistrations}
                items={groups.past}
                label="Past"
                onCancelSuccess={refetch}
                variant="past"
              />
            </div>
          )}
        </>
      )}
    </MainPageWrapper>
  );
};

export default MyRegistrationsPage;
